import React, { useEffect, useMemo, useState } from 'react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useNavigate } from 'react-router-dom';
import Dropdown from '@/components/dropdown/Dropdown';
import { useI18n } from '@/i18n';
import './register.css';

const API_BASE = (((import.meta as any).env?.VITE_API_URL) || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/+$/, '');

// Types for reference data
type Level = { id: number; level: string };
type Category = { id: number; category: string };
type Department = { id: number; category: number; department: string };

interface ReferenceData {
  levels: Level[];
  categories: Category[];
  departments: Department[];
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useI18n();
  useDocumentTitle(t('app.title'));

  // UI state
  const [mounted, setMounted] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [levelId, setLevelId] = useState<number | ''>('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const [phone, setPhone] = useState('');
  const [mail, setMail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordWarning, setPasswordWarning] = useState<string | null>(null);

  // Data state
  const [refData, setRefData] = useState<ReferenceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/register/reference-data`, { credentials: 'include' });
      if (!res.ok) throw new Error(t('register.errors.loadFailed'));
      const data: ReferenceData = await res.json();
      setRefData(data);
      setError(null);
    } catch (e: any) {
      setError(e.message || t('register.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!refData) fetchData();
  }, []);

  // Filter departments when category changes
  const filteredDepartments = useMemo(() => {
    if (!refData) return [] as Department[];
    if (!categoryId) return refData.departments;
    return refData.departments.filter((d) => d.category === Number(categoryId));
  }, [refData, categoryId]);


  // Phone input: enforce prefix 998, allow only 9 additional digits (total 12)
  const onPhoneChange = (v: string) => {
    const digitsOnly = v.replace(/\D+/g, '');
    // Strip any leading 998 occurrences to avoid duplication when pasting
    const rest = digitsOnly.replace(/^998+/, '');
    // Compose as 998 + up to 9 more digits
    const composed = '998' + rest.slice(0, 9);
    setPhone(composed);
  };

  // Password input: enforce English characters only and detect Russian
  const onPasswordChange = (v: string) => {
    setPassword(v);
    
    // Check for Russian characters
    const hasRussian = /[а-яёА-ЯЁ]/.test(v);
    if (hasRussian) {
      setPasswordWarning(t('register.warnings.passwordEnglishOnly'));
    } else {
      setPasswordWarning(null);
    }
  };

  // Confirm password input: enforce English characters only and detect Russian
  const onConfirmPasswordChange = (v: string) => {
    setConfirmPassword(v);
    
    // Check for Russian characters
    const hasRussian = /[а-яёА-ЯЁ]/.test(v);
    if (hasRussian) {
      setPasswordWarning(t('register.warnings.passwordEnglishOnly'));
    } else if (password && !/[а-яёА-ЯЁ]/.test(password)) {
      setPasswordWarning(null);
    }
  };

  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const translateRegisterError = (detail?: string) => {
    if (!detail) return null;
    const message = detail.trim();
    if (message.startsWith('Registration failed:')) {
      return t('register.errors.registrationFailed');
    }
    const map: Record<string, string> = {
      'Phone must start with 998 and contain exactly 12 digits': 'register.errors.phoneInvalid',
      'Password must be at least 8 characters long': 'register.errors.passwordMin',
      'Invalid language code': 'register.errors.invalidLanguage',
      'Selected level does not exist. Please refresh and choose a valid level.': 'register.errors.invalidLevel',
      'Selected department does not exist. Please refresh and choose a valid department.': 'register.errors.invalidDepartment',
      'This phone number is already registered.': 'register.errors.phoneTaken',
      'This email is already registered.': 'register.errors.emailTaken',
      'Registration failed to return user': 'register.errors.registrationFailed',
      'Invalid level or department_id. Please select valid reference values.': 'register.errors.invalidReference',
      'Failed to generate unique user_id, please try again': 'register.errors.userIdFailed',
      'Registration failed': 'register.errors.registrationFailed'
    };
    const key = map[message];
    return key ? t(key) : null;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate required fields
    if (!levelId) {
      setError(t('register.errors.selectLevel'));
      return;
    }
    if (!categoryId) {
      setError(t('register.errors.selectCategory'));
      return;
    }
    if (!departmentId) {
      setError(t('register.errors.selectDepartment'));
      return;
    }
    
    // Validate phone: must start with 998 and have 9 additional digits (total 12 digits)
    if (!/^998\d{9}$/.test(phone)) {
      setError(t('register.errors.phoneInvalid'));
      return;
    }
    // Validate password: minimum 8 characters and English only
    if (!password || password.length < 8) {
      setError(t('register.errors.passwordMin'));
      return;
    }
    // Check for Russian characters - block submission but don't set error (use warning)
    if (/[а-яёА-ЯЁ]/.test(password)) {
      setError(t('register.errors.passwordEnglish'));
      return;
    }
    // Validate password confirmation
    if (!confirmPassword) {
      setError(t('register.errors.confirmPassword'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('register.errors.passwordMismatch'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/register/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          surname,
          level: Number(levelId),
          department_id: Number(departmentId),
          phone,
          mail,
          password,
          lang: language,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Registration failed');
      setSuccessMsg(t('register.success'));
      // Reset form
      setName('');
      setSurname('');
      setLevelId('');
      setCategoryId('');
      setDepartmentId('');
      setPhone('');
      setMail('');
      setPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      setShowConfirmPassword(false);
      setPasswordWarning(null);
      // Redirect after short delay
      setTimeout(() => navigate('/'), 1500);
    } catch (err: any) {
      const detail = err?.message;
      const translated = translateRegisterError(detail);
      setError(translated || detail || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="register-page">
        <div className={`register-panel ${mounted ? 'show' : ''}`}>
          <div className="register-header">
            <div className="register-title">
              <h2>{t('register.title')}</h2>
            </div>
            <button className="btn-link" onClick={() => navigate('/')}>{t('register.backHome')}</button>
          </div>

        {loading && <div className="status note">{t('register.loading')}</div>}
        {error && <div className="status error">{error}</div>}
        {successMsg && <div className="status success">{successMsg}</div>}

        <form className="register-form" onSubmit={onSubmit}>
          {/* Name */}
          <div className="field">
            <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder=" " required />
            <label htmlFor="name">{t('register.name')}</label>
          </div>

          {/* Surname */}
          <div className="field">
            <input id="surname" type="text" value={surname} onChange={(e) => setSurname(e.target.value)} placeholder=" " required />
            <label htmlFor="surname">{t('register.surname')}</label>
          </div>

          {/* Level - custom dropdown */}
          <div className="field">
            <Dropdown
              id="register-level"
              label={t('register.level')}
              value={levelId ? String(levelId) : ''}
              options={(refData?.levels || []).map((l) => ({ label: l.level, value: String(l.id) }))}
              placeholder=""
              onChange={(next) => setLevelId(Number(next))}
            />
          </div>

          {/* Category - custom dropdown */}
          <div className="field">
            <Dropdown
              id="register-category"
              label={t('register.category')}
              value={categoryId ? String(categoryId) : ''}
              options={(refData?.categories || []).map((c) => ({ label: c.category, value: String(c.id) }))}
              placeholder=""
              onChange={(next) => { setCategoryId(Number(next)); setDepartmentId(''); }}
            />
          </div>

          {/* Department: only show after category selected */}
          {categoryId && (
            <div className="field">
              <Dropdown
                id="register-department"
                label={t('register.department')}
                value={departmentId ? String(departmentId) : ''}
                options={filteredDepartments.map((d) => ({ label: d.department, value: String(d.id) }))}
                placeholder=""
                onChange={(next) => setDepartmentId(Number(next))}
              />
            </div>
          )}

          {/* Phone */}
          <div className="field">
            <input
              id="phone"
              type="text"
              inputMode="numeric"
              maxLength={12}
              value={phone}
              onChange={(e) => onPhoneChange(e.target.value)}
              placeholder=" "
              required
            />
            <label htmlFor="phone">{t('register.phone')}</label>
          </div>

          {/* Mail */}
          <div className="field">
            <input id="mail" type="email" value={mail} onChange={(e) => setMail(e.target.value)} placeholder=" " required />
            <label htmlFor="mail">{t('register.mail')}</label>
          </div>

          {/* Password */}
          <div className="field password-field">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              placeholder=" "
              required
              minLength={8}
            />
            <label htmlFor="password">{t('register.password')}</label>
            <button
              type="button"
              className={`visibility-toggle ${showPassword ? 'visible' : ''}`}
              onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
            />
            {passwordWarning && (
              <div className="field-warning">{passwordWarning}</div>
            )}
          </div>

          {/* Confirm Password - show only when password has value */}
          {password && (
            <div className="field password-field">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => onConfirmPasswordChange(e.target.value)}
                placeholder=" "
                required
                minLength={8}
              />
              <label htmlFor="confirmPassword">{t('register.confirmPassword')}</label>
              <button
                type="button"
                className={`visibility-toggle ${showConfirmPassword ? 'visible' : ''}`}
                onClick={() => setShowConfirmPassword(v => !v)}
                aria-label={showConfirmPassword ? t('login.hidePassword') : t('login.showPassword')}
              />
            </div>
          )}

          <div className="actions">
            <button type="submit" className="btn btn-register" disabled={submitting}>{submitting ? t('register.working') : t('register.registerButton')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
